(function attachRegiosHelper(windowObject, documentObject) {
  'use strict';

  /*
    RegiosHelper is the theme-owned integration layer around Regios DOPP.

    Reference:
    - See REGIOS_DOPP_API.md in the repo root for the documented Regios events,
      external triggers, and API surface this helper is built around.

    Internal model:
    - API readiness: ensureApi()
    - Pricing calculation: getDiscountedPricing()
    - DOM rendering: updatePriceUI()

    Public entrypoints:
    - updatePriceUI({ product, selectedVariantId, container, selectors? })
      Use for a single known DOM container when you already have product data.
    - updatePricingForItems({ root, itemSelector, selectors?, revealRoot? })
      Use for DOM-driven bulk updates. Each matched item must have:
      data-regios-product="<serialized product json>"
      data-regios-variant-id="<variant id>"
    - syncVariantPricing({ product, selectedVariantId, container, quantity? })
      Use on PDP variant changes. This prefers dispatching the Regios variant event
      and falls back to direct DOM rendering in modal contexts.
    - updatePricingFromEvent(event, containerSelector)
      Use for quickshop/edit-cart/build-kit flows where product data comes from an event.
    - syncCollectionItems({ products, root, uniqueKey?, sentItemKeys? })
      Use for PLP/React flows that need to tell Regios about newly rendered collection items.

    How to use:
    - Prefer updatePricingForItems() for server-rendered sections with repeated product tiles.
    - Prefer updatePriceUI() for one-off containers that already have parsed product data.
    - Pass selectors when the markup does not use the default theme price classes.
    - Do not wait for the Regios API in calling code; the helper handles that internally.
    - Excluded products tagged with excludedFromPromotions are ignored by pricing updates.
  */

  const GLOBAL_NAME = 'RegiosHelper';
  const API_INITIALIZED_EVENT = 'regios-dopp:api-initialized';
  const PRICE_UPDATE_EVENT = 'regios-dopp:price-update';
  const EXCLUDED_PRODUCT_TAG = 'excludedFromPromotions';
  const THEME_PRICE_ON_SALE_CLASS = 'price--on-sale';
  const REGIOS_SALE_STATE_CLASS = 'regios-dopp-generic-price-item--sale';
  const REGIOS_CONFIG_SCRIPT_ID = 'regios-dopp-config';
  const BOGO_SUPPRESSED_PRICE_ATTRIBUTE = 'data-regios-bogo-sale';
  const MEMBERSHIP_LOADING_ATTRIBUTE = 'data-regios-membership-loading';

  const helper = windowObject[GLOBAL_NAME] || {};
  let apiReadyPromise = null;
  let regiosConfigCache = null;
  let regiosBogoCollectionCache = null;
  let bogoSuppressionRefreshQueued = false;
  const pendingBogoSuppressionRoots = new Set();
  const observedBogoSuppressionRoots = new WeakSet();
  const membershipFetchPromisesByHandle = new Map();

  function getRegiosApi() {
    return windowObject.RegiosDOPP?.api?.v0 || null;
  }

  function getApiInitializationTimeoutMs() {
    const delay = Number(windowObject.RegiosDOPP?.delay) || 0;
    return Math.max(10000, delay + 900);
  }

  function ensureApi() {
    const timeoutMs = getApiInitializationTimeoutMs();
    const existingApi = getRegiosApi();
    if (existingApi?.calculateDiscountedPrices) {
      return Promise.resolve(existingApi);
    }

    if (apiReadyPromise) {
      return apiReadyPromise;
    }

    apiReadyPromise = new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;

      function cleanup() {
        if (timeoutId) windowObject.clearTimeout(timeoutId);
        windowObject.removeEventListener(API_INITIALIZED_EVENT, onInitialized);
      }

      function finish(api) {
        if (settled) return;
        settled = true;
        cleanup();
        const readyApi = api?.calculateDiscountedPrices ? api : null;
        if (!readyApi) {
          console.warn('RegiosHelper.ensureApi failed to initialize Regios API before timeout');
          apiReadyPromise = null;
        }
        resolve(readyApi);
      }

      function onInitialized(event) {
        finish(event?.detail?.api || getRegiosApi());
      }

      windowObject.addEventListener(API_INITIALIZED_EVENT, onInitialized, { once: true });
      timeoutId = windowObject.setTimeout(() => finish(getRegiosApi()), timeoutMs);
    });

    return apiReadyPromise;
  }

  function getScope(root) {
    return root instanceof Element ? root : documentObject;
  }

  function parseJsonSafely(value) {
    if (!value || typeof value !== 'string') return null;

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function getRegiosConfig() {
    if (regiosConfigCache !== null) return regiosConfigCache;

    const configElement = documentObject.getElementById(REGIOS_CONFIG_SCRIPT_ID);
    regiosConfigCache = parseJsonSafely(configElement?.textContent) || {};
    return regiosConfigCache;
  }

  function isActiveDiscountConfig(discountConfig) {
    if (!discountConfig?.config?.isEnabled) return false;

    const nowMs = Date.now();
    const startMs = Date.parse(discountConfig.startDate);
    const endMs = discountConfig.endDate ? Date.parse(discountConfig.endDate) : null;

    if (Number.isFinite(startMs) && startMs > nowMs) return false;
    if (Number.isFinite(endMs) && endMs < nowMs) return false;

    const discountClasses = Array.isArray(discountConfig.discountClasses)
      ? discountConfig.discountClasses
      : [];

    return discountConfig.discountClass === 'PRODUCT' || discountClasses.includes('PRODUCT');
  }

  function normalizeIdList(value) {
    if (!value) return [];

    return String(value)
      .split(',')
      .map((entry) => Number(entry.trim()))
      .filter((entry) => Number.isFinite(entry) && entry > 0);
  }

  function addNormalizedIds(targetSet, value) {
    if (!(targetSet instanceof Set) || value == null) return;

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry == null) return;

        if (typeof entry === 'object') {
          const rawId = entry.id ?? entry.collectionId ?? entry.collection_id ?? entry.value;
          const normalized = Number(String(rawId || '').split('/').pop());
          if (Number.isFinite(normalized) && normalized > 0) {
            targetSet.add(normalized);
          }
          return;
        }

        normalizeIdList(entry).forEach((id) => targetSet.add(id));
      });
      return;
    }

    if (typeof value === 'object') {
      addNormalizedIds(targetSet, Object.values(value));
      return;
    }

    normalizeIdList(value).forEach((id) => targetSet.add(id));
  }

  function addCollectionIdsFromEligibility(targetSet, eligibility) {
    if (!(targetSet instanceof Set) || !eligibility) return;

    normalizeIdList(eligibility.asListOfIds).forEach((collectionId) => {
      targetSet.add(collectionId);
    });

    if (Array.isArray(eligibility.selectedCollections)) {
      eligibility.selectedCollections.forEach((collection) => {
        const rawId = collection?.id ?? collection?.collectionId;
        const normalized = Number(String(rawId || '').split('/').pop());
        if (Number.isFinite(normalized) && normalized > 0) {
          targetSet.add(normalized);
        }
      });
    }
  }

  function collectBogoCollectionIdsFromNode(node, targetSet) {
    if (!node || !(targetSet instanceof Set)) return;

    if (node.type === 'BUY_X') {
      const thenNode = node.then;
      const discountValue = thenNode?.value;
      const isBogoGetY =
        thenNode?.type === 'APPLY_DISCOUNT' &&
        discountValue?.type === 'GET_Y';

      if (isBogoGetY) {
        const requirementCollectionIds = new Set();
        const customerGetsCollectionIds = new Set();

        if (Array.isArray(node.requirements)) {
          node.requirements.forEach((requirement) => {
            addCollectionIdsFromEligibility(requirementCollectionIds, requirement?.productEligibility);
          });
        }

        addCollectionIdsFromEligibility(customerGetsCollectionIds, discountValue?.customerGets);

        requirementCollectionIds.forEach((collectionId) => targetSet.add(collectionId));
        customerGetsCollectionIds.forEach((collectionId) => targetSet.add(collectionId));
      }
    }

    collectBogoCollectionIdsFromNode(node.entryPoint, targetSet);
    collectBogoCollectionIdsFromNode(node.then, targetSet);
    collectBogoCollectionIdsFromNode(node.otherwise, targetSet);
  }

  function getBogoCollectionIds() {
    if (regiosBogoCollectionCache !== null) return regiosBogoCollectionCache;

    const config = getRegiosConfig();
    const discountConfigs = config?.discountConfigs || {};
    const collectionIds = new Set();

    Object.values(discountConfigs).forEach((serializedConfig) => {
      const parsedConfig = parseJsonSafely(serializedConfig);
      if (!isActiveDiscountConfig(parsedConfig)) return;

      collectBogoCollectionIdsFromNode(parsedConfig?.config?.customLogic, collectionIds);
    });

    regiosBogoCollectionCache = collectionIds;
    return regiosBogoCollectionCache;
  }

  function hasActiveBogoCollections() {
    return getBogoCollectionIds().size > 0;
  }

  function updateBogoCollectionGridState() {
    const collectionGrid = documentObject.getElementById('plpapp');
    if (!(collectionGrid instanceof Element)) return false;

    const collectionId = Number(collectionGrid.getAttribute('data-collection-id'));
    const isBogoCollection =
      Number.isFinite(collectionId) &&
      collectionId > 0 &&
      getBogoCollectionIds().has(collectionId);

    collectionGrid.classList.toggle('regios-bogo-collection-page', Boolean(isBogoCollection));
    return Boolean(isBogoCollection);
  }

  function getProductCollectionIds(product, container) {
    const collectionIds = new Set();
    const productId =
      getProductId(product) ||
      Number(
        container?.closest?.('[data-shopify-product-id]')?.getAttribute?.('data-shopify-product-id')
      ) ||
      Number(
        container?.closest?.('[data-regios-dopp-generic-product-id]')?.getAttribute?.('data-regios-dopp-generic-product-id')
      );
    const productHandle =
      product?.handle ||
      getHandleFromUrl(product?.url) ||
      container?.closest?.('[data-product-handle]')?.getAttribute?.('data-product-handle') ||
      getHandleFromUrl(
        container?.closest?.('.product-tile, .product-carousel__recommendation, .category-products__product-tile')
          ?.querySelector?.('a[href*="/products/"]')
          ?.getAttribute?.('href')
      );
    const membershipMap = windowObject.regiosProductCollectionMembershipByProductId || {};
    const membershipByHandleMap = windowObject.regiosProductCollectionMembershipByHandle || {};

    addNormalizedIds(collectionIds, product?.collectionIds);
    addNormalizedIds(collectionIds, product?.collection_ids);
    addNormalizedIds(collectionIds, product?.collections);
    addNormalizedIds(collectionIds, product?.attributes?.collectionIds);
    addNormalizedIds(collectionIds, product?.attributes?.collection_ids);
    addNormalizedIds(collectionIds, product?.attributes?.collections);
    addNormalizedIds(collectionIds, container?.getAttribute?.('data-regios-collection-ids'));

    const nearestCollectionSource = container?.closest?.('[data-regios-collection-ids]');
    if (nearestCollectionSource && nearestCollectionSource !== container) {
      addNormalizedIds(collectionIds, nearestCollectionSource.getAttribute('data-regios-collection-ids'));
    }

    if (Number.isFinite(productId) && productId > 0) {
      addNormalizedIds(collectionIds, membershipMap[productId]);
    }

    if (productHandle) {
      addNormalizedIds(collectionIds, membershipByHandleMap[productHandle]);
    }

    return collectionIds;
  }

  function getProductHandle(product, container) {
    return (
      product?.handle ||
      getHandleFromUrl(product?.url) ||
      container?.closest?.('[data-product-handle]')?.getAttribute?.('data-product-handle') ||
      getHandleFromUrl(
        container?.closest?.('.product-tile, .product-carousel__recommendation, .category-products__product-tile')
          ?.querySelector?.('a[href*="/products/"]')
          ?.getAttribute?.('href')
      ) ||
      ''
    );
  }

  async function fetchCollectionMembershipByHandle(productHandle) {
    if (!productHandle) return [];

    const membershipByHandleMap =
      (windowObject.regiosProductCollectionMembershipByHandle =
        windowObject.regiosProductCollectionMembershipByHandle || {});

    if (membershipByHandleMap[productHandle]) {
      return membershipByHandleMap[productHandle];
    }

    if (membershipFetchPromisesByHandle.has(productHandle)) {
      return membershipFetchPromisesByHandle.get(productHandle);
    }

    const fetchPromise = fetch(`${windowObject.Shopify?.routes?.root || '/'}products/${productHandle}`, {
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) {
          membershipByHandleMap[productHandle] = [];
          return membershipByHandleMap[productHandle];
        }

        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const productInfo = parsedDocument.querySelector('product-info[data-regios-collection-ids]');
        const collectionIds = normalizeIdList(productInfo?.getAttribute('data-regios-collection-ids'));

        membershipByHandleMap[productHandle] = collectionIds;
        return membershipByHandleMap[productHandle];
      })
      .catch(() => {
        membershipByHandleMap[productHandle] = [];
        return membershipByHandleMap[productHandle];
      })
      .finally(() => {
        membershipFetchPromisesByHandle.delete(productHandle);
      });

    membershipFetchPromisesByHandle.set(productHandle, fetchPromise);
    return fetchPromise;
  }

  function getQuantityValue(root) {
    const scope = getScope(root);
    const quantityInput = scope.querySelector('input[name="quantity"], input#quantity, select[name="quantity"]');
    const quantity = Number(quantityInput?.value);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function getCurrencyCode() {
    return (
      windowObject.themeCurrency?.isoCode ||
      windowObject.Shopify?.currency?.active ||
      documentObject.documentElement?.getAttribute('data-shop-currency') ||
      'USD'
    );
  }

  function formatRegiosAmount(amount, fallback = '') {
    if (!Number.isFinite(amount)) return fallback;
    if (typeof windowObject.TarteCurrencyFormatter?.formatAmount !== 'function') return fallback;

    return windowObject.TarteCurrencyFormatter.formatAmount(amount, {
      currency: getCurrencyCode(),
      trimTrailingZeros: true,
      fallback,
    });
  }

  function isBogoCollectionPageContext(container) {
    const collectionGrid = container?.closest?.('#plpapp.regios-bogo-collection-page');
    if (!collectionGrid) return false;

    const collectionId = Number(collectionGrid.getAttribute('data-collection-id'));
    return Number.isFinite(collectionId) && collectionId > 0 && getBogoCollectionIds().has(collectionId);
  }

  function isBogoProductContext(product, container) {
    const bogoCollectionIds = getBogoCollectionIds();
    if (!bogoCollectionIds.size) return false;

    const productCollectionIds = getProductCollectionIds(product, container);
    for (const collectionId of productCollectionIds) {
      if (bogoCollectionIds.has(collectionId)) return true;
    }

    return isBogoCollectionPageContext(container);
  }

  function setBogoSuppressedPriceState(priceBlock, isSuppressed) {
    if (!(priceBlock instanceof Element)) return;

    if (isSuppressed) {
      if (priceBlock.getAttribute(BOGO_SUPPRESSED_PRICE_ATTRIBUTE) === 'true') return;
      priceBlock.setAttribute(BOGO_SUPPRESSED_PRICE_ATTRIBUTE, 'true');
      return;
    }

    if (!priceBlock.hasAttribute(BOGO_SUPPRESSED_PRICE_ATTRIBUTE)) return;
    priceBlock.removeAttribute(BOGO_SUPPRESSED_PRICE_ATTRIBUTE);
  }

  function setMembershipLoadingState(priceBlock, isLoading) {
    if (!(priceBlock instanceof Element)) return;

    if (isLoading) {
      priceBlock.setAttribute(MEMBERSHIP_LOADING_ATTRIBUTE, 'true');
      return;
    }

    priceBlock.removeAttribute(MEMBERSHIP_LOADING_ATTRIBUTE);
  }

  function setRegiosSaleState(priceBlock, hasRegiosSale) {
    if (!(priceBlock instanceof Element)) return;

    priceBlock.classList.toggle(REGIOS_SALE_STATE_CLASS, Boolean(hasRegiosSale));
  }

  function hasRegiosSaleState(priceBlock) {
    if (!(priceBlock instanceof Element)) return false;

    return priceBlock.classList.contains(REGIOS_SALE_STATE_CLASS);
  }

  function refreshBogoSuppressionForPriceBlock(priceBlock) {
    if (!(priceBlock instanceof Element)) return;

    const shouldSuppress =
      hasRegiosSaleState(priceBlock) &&
      isBogoProductContext(null, priceBlock) &&
      !isModalContext(priceBlock);

    setBogoSuppressedPriceState(priceBlock, shouldSuppress);
    if (shouldSuppress) {
      setMembershipLoadingState(priceBlock, false);
      return;
    }

    if (!shouldSuppress && !isModalContext(priceBlock) && hasActiveBogoCollections()) {
      const productHandle = getProductHandle(null, priceBlock);
      const collectionIds = getProductCollectionIds(null, priceBlock);

      if (productHandle && !collectionIds.size) {
        setMembershipLoadingState(priceBlock, true);
        fetchCollectionMembershipByHandle(productHandle).then((resolvedCollectionIds) => {
          setMembershipLoadingState(priceBlock, false);
          if (resolvedCollectionIds?.length) {
            queueBogoSuppressionRefresh(priceBlock);
          }
        }).catch(() => {
          setMembershipLoadingState(priceBlock, false);
        });
        return;
      }
    }

    setMembershipLoadingState(priceBlock, false);
  }

  function refreshBogoSuppression(root) {
    const scope = getScope(root);

    if (scope.matches?.('.regios-dopp-generic-price-container')) {
      refreshBogoSuppressionForPriceBlock(scope);
    }

    scope.querySelectorAll?.('.regios-dopp-generic-price-container').forEach((priceBlock) => {
      refreshBogoSuppressionForPriceBlock(priceBlock);
    });
  }

  function queueBogoSuppressionRefresh(root) {
    const scope = getScope(root);
    if (!(scope instanceof Element || scope instanceof Document)) return;

    pendingBogoSuppressionRoots.add(scope);
    if (bogoSuppressionRefreshQueued) return;

    bogoSuppressionRefreshQueued = true;
    windowObject.requestAnimationFrame(() => {
      bogoSuppressionRefreshQueued = false;
      const roots = Array.from(pendingBogoSuppressionRoots);
      pendingBogoSuppressionRoots.clear();
      roots.forEach((pendingRoot) => {
        refreshBogoSuppression(pendingRoot);
      });
    });
  }

  function observeBogoSuppression(root) {
    if (!(root instanceof Element || root instanceof Document)) return null;

    const scope = getScope(root);
    if (!(scope instanceof Element || scope instanceof Document)) return null;
    if (observedBogoSuppressionRoots.has(scope)) return null;

    observedBogoSuppressionRoots.add(scope);

    const observer = new MutationObserver(() => {
      queueBogoSuppressionRefresh(scope);
    });

    observer.observe(scope, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-regios-bogo-sale'],
    });

    return observer;
  }

  function reformatPriceContainer(priceContainer, priceUpdateArgs = {}, discountWasApplied = false) {
    if (!(priceContainer instanceof Element)) return;

    const priceElements = getPriceElements(priceContainer);
    const { salePriceElement, strikePriceElement, regularPriceDisplayElement } = priceElements;
    const regularAmount = Number(priceUpdateArgs.regularPrice);
    const saleAmount = Number(priceUpdateArgs.salePrice);

    // Guard against invalid zero/negative sale values returned by Regios.
    // If we see a discount flagged but the sale price is <= 0 while a valid regular price exists,
    // treat it as no discount and restore the regular price everywhere.
    const invalidZeroSale =
      Boolean(discountWasApplied) &&
      Number.isFinite(saleAmount) &&
      saleAmount <= 0 &&
      Number.isFinite(regularAmount) &&
      regularAmount > 0;

    if (invalidZeroSale) {
      setThemeSaleState(priceContainer, false);
      setRegiosSaleState(priceContainer, false);
      if (salePriceElement) {
        salePriceElement.textContent = formatRegiosAmount(
          regularAmount,
          salePriceElement.textContent?.trim() || ''
        );
      }
      if (strikePriceElement) {
        strikePriceElement.textContent = '';
      }
      if (regularPriceDisplayElement) {
        regularPriceDisplayElement.textContent = formatRegiosAmount(
          regularAmount,
          regularPriceDisplayElement.textContent?.trim() || ''
        );
      }
      return;
    }

    const hasDiscount =
      Boolean(discountWasApplied) &&
      Number.isFinite(saleAmount) &&
      Number.isFinite(regularAmount) &&
      saleAmount < regularAmount;

    const suppressBogoSale = hasDiscount && isBogoProductContext(null, priceContainer);
    setBogoSuppressedPriceState(priceContainer, suppressBogoSale);
    setRegiosSaleState(priceContainer, hasDiscount);

    if (!hasDiscount) return;

    if (salePriceElement && salePriceElement.textContent?.trim()) {
      if (Number.isFinite(saleAmount)) {
        salePriceElement.textContent = formatRegiosAmount(saleAmount, salePriceElement.textContent.trim());
      }
    }

    if (strikePriceElement && strikePriceElement.textContent?.trim() && Number.isFinite(regularAmount)) {
      strikePriceElement.textContent = formatRegiosAmount(regularAmount, strikePriceElement.textContent.trim());
    }

    if (
      regularPriceDisplayElement &&
      regularPriceDisplayElement.textContent?.trim() &&
      Number.isFinite(regularAmount)
    ) {
      regularPriceDisplayElement.textContent = formatRegiosAmount(
        regularAmount,
        regularPriceDisplayElement.textContent.trim()
      );
    }
  }

  function attachPriceUpdateFormatterListener() {
    if (helper._priceUpdateFormatterBound) return;

    windowObject.addEventListener(PRICE_UPDATE_EVENT, (event) => {
      const detail = event?.detail;
      if (!detail?.priceContainer || !detail?.priceUpdateArgs) return;

      windowObject.requestAnimationFrame(() => {
        reformatPriceContainer(
          detail.priceContainer,
          detail.priceUpdateArgs,
          detail.discountWasApplied
        );
        refreshBogoSuppression(detail.priceContainer);
      });
    });

    helper._priceUpdateFormatterBound = true;
  }

  function setPriceContainersVisible(root) {
    const scope = getScope(root);
    scope.querySelectorAll('.price__container').forEach((priceContainer) => {
      priceContainer.style.visibility = 'visible';
    });
  }

  function toCents(amount) {
    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount) ? Math.round(numericAmount * 100) : null;
  }

  function getHandleFromUrl(url) {
    if (!url) return '';

    const cleanUrl = String(url).split('?')[0].replace(/\/+$/, '');
    const segments = cleanUrl.split('/').filter(Boolean);
    return segments[segments.length - 1] || '';
  }

  function getProductId(product) {
    return Number(product?.productId || product?.id);
  }

  function getVariant(product, selectedVariantId) {
    if (!product?.variants?.length) return null;

    return (
      product.variants.find((variant) => Number(variant.id) === Number(selectedVariantId)) || null
    );
  }

  function isExcludedProduct(product) {
    return Boolean(product?.tags?.includes?.(EXCLUDED_PRODUCT_TAG));
  }

  function isModalContext(container) {
    return Boolean(
      container?.closest?.('#quickshop, #quickshop-cart, #buildkit-quickshop, #custom-kit-quickshop')
    );
  }

  function buildCollectionItem(product, collectionId) {
    const selectedVariant = product?.variants?.[0];
    const variantId = Number(selectedVariant?.variantId ?? selectedVariant?.id);
    const productId = Number(product?.productId ?? product?.id);
    const regularPriceInCents =
      toCents(selectedVariant?.sku_price) ??
      toCents(product?.price?.amount);

    if (!Number.isFinite(productId) || !Number.isFinite(variantId) || !Number.isFinite(regularPriceInCents)) {
      return null;
    }

    const compareAtPriceInCents =
      toCents(selectedVariant?.compareAtPrice) ??
      toCents(selectedVariant?.sku_compare_at_price) ??
      null;

    return {
      productId,
      variantId,
      regularPriceInCents,
      compareAtPriceInCents,
      collectionIds: Number.isFinite(collectionId) ? [collectionId] : [],
      handle: product?.handle || getHandleFromUrl(product?.url),
      vendor: product?.vendor || product?.productVendor || '',
      url: product?.url || '',
      tags: Array.isArray(product?.tags) ? product.tags : [],
      variants: Array.isArray(product?.variants)
        ? product.variants
            .map((variant) => {
              const id = Number(variant?.variantId ?? variant?.id);
              const priceInCents = toCents(variant?.sku_price);

              if (!Number.isFinite(id) || !Number.isFinite(priceInCents)) return null;

              return {
                id,
                title: variant?.title || variant?.name || '',
                priceInCents,
                compareAtPriceInCents:
                  toCents(variant?.compareAtPrice) ??
                  toCents(variant?.sku_compare_at_price) ??
                  null,
              };
            })
            .filter(Boolean)
        : [],
    };
  }

  function dispatchVariantChange({ product, selectedVariantId, quantity, container }) {
    if (!product || !selectedVariantId) return false;
    if (isModalContext(container)) return false;

    const variant = getVariant(product, selectedVariantId);
    const detail = {
      selectedVariant: {
        id: Number(selectedVariantId),
      },
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : getQuantityValue(container),
    };

    if (variant?.priceInCents != null) {
      detail.selectedVariant.price = Number(variant.priceInCents);
    } else if (variant?.price != null) {
      detail.selectedVariant.price = Number(variant.price);
    }

    windowObject.dispatchEvent(
      new CustomEvent('regios-dopp:variant-change', {
        detail,
      })
    );

    return true;
  }

  async function getDiscountedPricing({
    product,
    selectedVariantId,
    regularPriceInCents,
    fallbackRegularPriceText = '',
  }) {
    try {
      const api = await ensureApi();
      if (!api?.calculateDiscountedPrices) return null;

      const { salePrice, regularPrice } = await api.calculateDiscountedPrices({
        productId: getProductId(product),
        variantId: Number(selectedVariantId),
        regularPriceInCents: Number(regularPriceInCents),
        insertAt: 'LAST',
      });

      const saleAmount = Number(salePrice?.amount);
      const regularAmount = Number(regularPrice?.amount);
      const hasRegiosSale =
        Number.isFinite(saleAmount) &&
        Number.isFinite(regularAmount) &&
        saleAmount > 0 &&
        saleAmount < regularAmount;

      return {
        saleAmount,
        regularAmount,
        hasRegiosSale,
        formattedSalePrice: hasRegiosSale ? formatRegiosAmount(saleAmount, salePrice?.formatted) : null,
        formattedRegularPrice: formatRegiosAmount(regularAmount, regularPrice?.formatted || fallbackRegularPriceText),
      };
    } catch (error) {
      console.warn('Regios price update failed:', error);
      return null;
    }
  }

  function getPriceBlock(container, selectors = {}) {
    if (!(container instanceof Element)) return null;

    const priceBlockSelector = selectors.priceBlock || '.price-block, .regios-dopp-generic-price-container';
    return container.querySelector(priceBlockSelector);
  }

  function getPriceElements(priceBlock, selectors = {}) {
    if (!priceBlock) return null;

    const salePriceElement = priceBlock.querySelector(selectors.salePrice || '.price-item--sale');
    const strikePriceElement =
      priceBlock.querySelector(selectors.strikePrice || '.price__sale s.price-item--regular') ||
      (!selectors.strikePrice ? priceBlock.querySelector('.price-item--regular') : null);
    const regularPriceDisplayElement = priceBlock.querySelector(
      selectors.regularPriceDisplay || '.price__regular .price-item--regular'
    );

    return {
      salePriceElement,
      strikePriceElement,
      regularPriceDisplayElement,
    };
  }

  function setThemeSaleState(priceBlock, hasRegiosSale, selectors = {}) {
    if (!(priceBlock instanceof Element)) return;

    priceBlock.classList.toggle(selectors.saleStateClass || THEME_PRICE_ON_SALE_CLASS, Boolean(hasRegiosSale));
  }

  async function updatePriceUI({
    product,
    selectedVariantId,
    container,
    selectors,
  }) {
    if (!product || !selectedVariantId || !(container instanceof Element)) return;
    if (isExcludedProduct(product)) return;

    const variant = getVariant(product, selectedVariantId);
    const priceBlock = getPriceBlock(container, selectors);
    if (!priceBlock) return;

    const { salePriceElement, strikePriceElement, regularPriceDisplayElement } =
      getPriceElements(priceBlock, selectors);
    const regiosPricing = await getDiscountedPricing({
      product,
      selectedVariantId,
      regularPriceInCents: variant?.priceInCents ?? Number(product.price),
      fallbackRegularPriceText: regularPriceDisplayElement?.textContent?.trim() || '',
    });

    if (!regiosPricing) return;
    const suppressBogoSale = regiosPricing.hasRegiosSale && isBogoProductContext(product, container);
    setBogoSuppressedPriceState(priceBlock, suppressBogoSale);

    if (!regiosPricing.hasRegiosSale) {
      setRegiosSaleState(priceBlock, false);
      if (isModalContext(container)) {
        setThemeSaleState(priceBlock, false, selectors);
        if (regularPriceDisplayElement && regiosPricing.formattedRegularPrice) {
          regularPriceDisplayElement.textContent = regiosPricing.formattedRegularPrice;
        }
        if (salePriceElement && regiosPricing.formattedRegularPrice) {
          salePriceElement.textContent = regiosPricing.formattedRegularPrice;
        }
        if (strikePriceElement) {
          strikePriceElement.textContent = '';
        }
        if (selectors?.saleAppliedClass) {
          priceBlock.classList.remove(selectors.saleAppliedClass);
        } else {
          priceBlock.classList.remove(REGIOS_SALE_STATE_CLASS);
        }
      }
      setPriceContainersVisible(container || documentObject);
      return;
    }

    if (regiosPricing.formattedSalePrice) {
      setRegiosSaleState(priceBlock, true);
      setThemeSaleState(priceBlock, true, selectors);
      if (salePriceElement) {
        salePriceElement.textContent = regiosPricing.formattedSalePrice;
        if (selectors?.saleAppliedClass) {
          priceBlock.classList.add(selectors.saleAppliedClass);
        } else {
          priceBlock.classList.add(REGIOS_SALE_STATE_CLASS);
        }
      }
      if (strikePriceElement) {
        strikePriceElement.textContent = regiosPricing.formattedRegularPrice;
      }
    }

    setPriceContainersVisible(container || documentObject);
  }

  async function updatePriceUIWithFallback({
    product,
    selectedVariantId,
    container,
    selectors,
    revealRoot,
  }) {
    try {
      return await updatePriceUI({
        product,
        selectedVariantId,
        container,
        selectors,
      });
    } finally {
      setPriceContainersVisible(revealRoot || container || documentObject);
    }
  }

  async function updatePricingForItems({
    root,
    itemSelector,
    selectors,
    revealRoot,
  }) {
    const scope = getScope(root);
    const items = scope.querySelectorAll(itemSelector);

    if (!items.length) {
      setPriceContainersVisible(revealRoot || scope);
      return [];
    }

    const api = await ensureApi();
    if (!api?.calculateDiscountedPrices) {
      setPriceContainersVisible(revealRoot || scope);
      return [];
    }

    const updates = Array.from(items).map(async (item) => {
      const productJson = item.getAttribute('data-regios-product');
      const selectedVariantId = Number(item.getAttribute('data-regios-variant-id'));

      if (!productJson || !Number.isFinite(selectedVariantId) || selectedVariantId <= 0) return null;

      try {
        return await updatePriceUI({
          product: JSON.parse(productJson),
          selectedVariantId,
          container: item,
          selectors,
        });
      } catch (error) {
        console.warn('Failed to update Regios pricing for item:', error);
        return null;
      }
    });

    const results = await Promise.all(updates);
    setPriceContainersVisible(revealRoot || scope);
    return results;
  }

  async function syncVariantPricing({
    product,
    selectedVariantId,
    container,
    quantity,
  }) {
    if (!product || !selectedVariantId || !(container instanceof Element)) return;
    if (isExcludedProduct(product)) return;

    const didDispatch = dispatchVariantChange({
      product,
      selectedVariantId,
      quantity,
      container,
    });

    if (didDispatch) {
      return;
    }

    return updatePriceUI({
      product,
      selectedVariantId,
      container,
    });
  }

  async function syncCollectionItems({
    products,
    root,
    uniqueKey = 'default',
    sentItemKeys,
  }) {
    if (!Array.isArray(products) || !products.length) return [];

    const scope = getScope(root);
    const collectionId = Number(scope.getAttribute?.('data-collection-id'));
    const nextItems = products
      .map((product) => buildCollectionItem(product, collectionId))
      .filter((item) => {
        if (!item) return false;

        const itemKey = `${item.productId}:${item.variantId}`;
        if (sentItemKeys?.has(itemKey)) return false;

        return true;
      });

    if (!nextItems.length) return [];

    await ensureApi();
    await new Promise((resolve) => {
      windowObject.requestAnimationFrame(() => {
        windowObject.dispatchEvent(
          new CustomEvent('regios-dopp:collection-page:new-items', {
            detail: {
              uniqueKey,
              newItems: nextItems,
            },
          })
        );

        nextItems.forEach((item) => {
          sentItemKeys?.add(`${item.productId}:${item.variantId}`);
        });

        resolve();
      });
    });

    return nextItems;
  }

  async function updatePricingFromEvent(event, containerSelector) {
    const container = documentObject.querySelector(containerSelector);
    const { product, selectedProduct } = event?.detail || {};

    if (!container || !product || !selectedProduct) {
      if (container) {
        setPriceContainersVisible(container);
      }
      return;
    }

    return updatePriceUIWithFallback({
      product,
      selectedVariantId: selectedProduct,
      container,
      revealRoot: container,
    });
  }

  helper.getApi = getRegiosApi;
  helper.waitForApi = ensureApi;
  helper.setPriceContainersVisible = setPriceContainersVisible;
  helper.getDiscountedPricing = getDiscountedPricing;
  helper.updatePriceUI = updatePriceUI;
  helper.updatePriceUIWithFallback = updatePriceUIWithFallback;
  helper.updatePricingForItems = updatePricingForItems;
  helper.dispatchVariantChange = dispatchVariantChange;
  helper.syncVariantPricing = syncVariantPricing;
  helper.syncCollectionItems = syncCollectionItems;
  helper.updatePricingForContainer = updatePriceUI;
  helper.updatePricingFromEvent = updatePricingFromEvent;
  helper.refreshBogoSuppression = refreshBogoSuppression;
  helper.hasActiveBogoCollections = hasActiveBogoCollections;

  attachPriceUpdateFormatterListener();
  updateBogoCollectionGridState();
  refreshBogoSuppression(documentObject);
  observeBogoSuppression(documentObject.getElementById('plpapp'));
  observeBogoSuppression(documentObject.querySelector('product-info'));
  if (documentObject.readyState === 'loading') {
    documentObject.addEventListener('DOMContentLoaded', updateBogoCollectionGridState, { once: true });
    documentObject.addEventListener(
      'DOMContentLoaded',
      () => {
        refreshBogoSuppression(documentObject);
        observeBogoSuppression(documentObject.getElementById('plpapp'));
        observeBogoSuppression(documentObject.querySelector('product-info'));
      },
      { once: true }
    );
  }

  windowObject[GLOBAL_NAME] = helper;
})(window, document);
